import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  DatePicker,
  Descriptions,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EyeOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  listMeituanOrderConfirmLogs,
  type MeituanOrderConfirmLog as MeituanOrderConfirmLogRecord,
} from '../services/meituanOrderConfirmLogService';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const statusMap: Record<string, { text: string; color: string }> = {
  success: { text: '成功', color: 'green' },
  failed: { text: '失败', color: 'red' },
  skipped: { text: '跳过', color: 'orange' },
};

const resultMap: Record<number, { text: string; color: string }> = {
  1: { text: '接单', color: 'blue' },
  2: { text: '拒单', color: 'volcano' },
};

const formatDateTime = (value?: string) => {
  if (!value) return '-';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm:ss') : value;
};

const formatJSON = (value?: string) => {
  if (!value) return '-';
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
};

const MeituanOrderConfirmLog: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState<MeituanOrderConfirmLogRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [serviceOrderNo, setServiceOrderNo] = useState('');
  const [meituanOrderId, setMeituanOrderId] = useState('');
  const [appOrderId, setAppOrderId] = useState('');
  const [shopId, setShopId] = useState('');
  const [result, setResult] = useState<string>();
  const [status, setStatus] = useState<string>();
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [currentLog, setCurrentLog] = useState<MeituanOrderConfirmLogRecord | null>(null);

  const loadData = async (nextPage = page, nextPageSize = pageSize) => {
    setLoading(true);
    try {
      const res = await listMeituanOrderConfirmLogs({
        page: nextPage,
        pageSize: nextPageSize,
        serviceOrderNo: serviceOrderNo || undefined,
        meituanOrderId: meituanOrderId || undefined,
        appOrderId: appOrderId || undefined,
        shopId: shopId || undefined,
        result,
        status,
        startTime: dateRange?.[0]?.format('YYYY-MM-DD 00:00:00'),
        endTime: dateRange?.[1]?.format('YYYY-MM-DD 23:59:59'),
      });
      setLogs(res.data.list || []);
      setTotal(res.data.total || 0);
    } catch {
      message.error('加载美团接单日志失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, [page, pageSize]);

  const handleSearch = () => {
    setPage(1);
    void loadData(1, pageSize);
  };

  const handleReset = () => {
    setServiceOrderNo('');
    setMeituanOrderId('');
    setAppOrderId('');
    setShopId('');
    setResult(undefined);
    setStatus(undefined);
    setDateRange(null);
    setPage(1);
    setTimeout(() => {
      void loadData(1, pageSize);
    }, 0);
  };

  const openDetail = (record: MeituanOrderConfirmLogRecord) => {
    setCurrentLog(record);
    setDetailVisible(true);
  };

  const columns: ColumnsType<MeituanOrderConfirmLogRecord> = [
    {
      title: '平台订单号',
      dataIndex: 'serviceOrderNo',
      key: 'serviceOrderNo',
      width: 170,
      ellipsis: true,
      render: value => value || '-',
    },
    {
      title: '美团订单ID',
      dataIndex: 'meituanOrderId',
      key: 'meituanOrderId',
      width: 170,
      ellipsis: true,
      render: value => (
        <Tooltip title={value || '-'}>
          <Text>{value || '-'}</Text>
        </Tooltip>
      ),
    },
    {
      title: '三方订单号',
      dataIndex: 'appOrderId',
      key: 'appOrderId',
      width: 190,
      ellipsis: true,
      render: value => value || '-',
    },
    {
      title: '授权实体ID',
      dataIndex: 'shopId',
      key: 'shopId',
      width: 220,
      ellipsis: true,
      render: value => (
        <Tooltip title={value || '-'}>
          <Text>{value || '-'}</Text>
        </Tooltip>
      ),
    },
    {
      title: '门店编码',
      dataIndex: 'opPoiId',
      key: 'opPoiId',
      width: 220,
      ellipsis: true,
      render: value => (
        <Tooltip title={value || '-'}>
          <Text>{value || '-'}</Text>
        </Tooltip>
      ),
    },
    {
      title: '接单结果',
      dataIndex: 'result',
      key: 'result',
      width: 100,
      render: value => {
        const item = resultMap[value] || { text: value || '-', color: 'default' };
        return <Tag color={item.color}>{item.text}</Tag>;
      },
    },
    {
      title: '调用状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: value => {
        const item = statusMap[value] || { text: value || '-', color: 'default' };
        return <Tag color={item.color}>{item.text}</Tag>;
      },
    },
    {
      title: '自动接单',
      dataIndex: 'autoAcceptOrder',
      key: 'autoAcceptOrder',
      width: 100,
      render: value => <Tag color={value === 1 ? 'green' : 'default'}>{value === 1 ? '开启' : '关闭'}</Tag>,
    },
    {
      title: '错误信息',
      dataIndex: 'errorMessage',
      key: 'errorMessage',
      width: 220,
      ellipsis: true,
      render: value => value ? (
        <Tooltip title={value}>
          <Text type="danger">{value}</Text>
        </Tooltip>
      ) : '-',
    },
    {
      title: '成功时间',
      dataIndex: 'confirmedAt',
      key: 'confirmedAt',
      width: 170,
      render: value => formatDateTime(value),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 170,
      render: value => formatDateTime(value),
    },
    {
      title: '操作',
      key: 'action',
      width: 90,
      fixed: 'right',
      render: (_, record) => (
        <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => openDetail(record)}>
          详情
        </Button>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <Title level={2}>美团接单日志</Title>
      </div>

      <div className="page-content">
        <Card style={{ marginBottom: 16 }}>
          <Space wrap>
            <Input
              allowClear
              style={{ width: 180 }}
              placeholder="平台订单号"
              value={serviceOrderNo}
              onChange={event => setServiceOrderNo(event.target.value)}
              onPressEnter={handleSearch}
            />
            <Input
              allowClear
              style={{ width: 180 }}
              placeholder="美团订单ID"
              value={meituanOrderId}
              onChange={event => setMeituanOrderId(event.target.value)}
              onPressEnter={handleSearch}
            />
            <Input
              allowClear
              style={{ width: 180 }}
              placeholder="三方订单号"
              value={appOrderId}
              onChange={event => setAppOrderId(event.target.value)}
              onPressEnter={handleSearch}
            />
            <Input
              allowClear
              style={{ width: 220 }}
              placeholder="授权实体ID/门店"
              value={shopId}
              onChange={event => setShopId(event.target.value)}
              onPressEnter={handleSearch}
            />
            <Select
              allowClear
              style={{ width: 120 }}
              placeholder="接单结果"
              value={result}
              onChange={value => setResult(value)}
              options={[
                { label: '接单', value: '1' },
                { label: '拒单', value: '2' },
              ]}
            />
            <Select
              allowClear
              style={{ width: 120 }}
              placeholder="调用状态"
              value={status}
              onChange={value => setStatus(value)}
              options={[
                { label: '成功', value: 'success' },
                { label: '失败', value: 'failed' },
                { label: '跳过', value: 'skipped' },
              ]}
            />
            <RangePicker
              value={dateRange}
              onChange={dates => setDateRange(dates as [dayjs.Dayjs | null, dayjs.Dayjs | null])}
            />
            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
              搜索
            </Button>
            <Button icon={<ReloadOutlined />} onClick={handleReset}>
              重置
            </Button>
          </Space>
        </Card>

        <Card>
          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={logs}
            scroll={{ x: 1900 }}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: value => `共 ${value} 条`,
              onChange: (nextPage, nextPageSize) => {
                setPage(nextPage);
                setPageSize(nextPageSize || 10);
              },
            }}
          />
        </Card>
      </div>

      <Modal
        title="美团接单日志详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={<Button onClick={() => setDetailVisible(false)}>关闭</Button>}
        width={920}
      >
        {currentLog && (
          <>
            <Descriptions column={2} bordered size="small">
              <Descriptions.Item label="平台订单ID">{currentLog.serviceOrderId || '-'}</Descriptions.Item>
              <Descriptions.Item label="平台订单号">{currentLog.serviceOrderNo || '-'}</Descriptions.Item>
              <Descriptions.Item label="美团订单ID">{currentLog.meituanOrderId || '-'}</Descriptions.Item>
              <Descriptions.Item label="三方订单号">{currentLog.appOrderId || '-'}</Descriptions.Item>
              <Descriptions.Item label="授权实体ID">{currentLog.shopId || '-'}</Descriptions.Item>
              <Descriptions.Item label="门店编码">{currentLog.opPoiId || '-'}</Descriptions.Item>
              <Descriptions.Item label="接单结果">
                <Tag color={(resultMap[currentLog.result] || {}).color || 'default'}>
                  {(resultMap[currentLog.result] || {}).text || currentLog.result || '-'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="调用状态">
                <Tag color={(statusMap[currentLog.status] || {}).color || 'default'}>
                  {(statusMap[currentLog.status] || {}).text || currentLog.status || '-'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="自动接单">{currentLog.autoAcceptOrder === 1 ? '开启' : '关闭'}</Descriptions.Item>
              <Descriptions.Item label="成功时间">{formatDateTime(currentLog.confirmedAt)}</Descriptions.Item>
              <Descriptions.Item label="创建时间">{formatDateTime(currentLog.createdAt)}</Descriptions.Item>
              <Descriptions.Item label="更新时间">{formatDateTime(currentLog.updatedAt)}</Descriptions.Item>
              {currentLog.errorMessage && (
                <Descriptions.Item label="错误信息" span={2}>
                  <Text type="danger">{currentLog.errorMessage}</Text>
                </Descriptions.Item>
              )}
            </Descriptions>

            <div style={{ marginTop: 16 }}>
              <div style={{ fontWeight: 500, marginBottom: 8 }}>请求参数</div>
              <pre style={{ maxHeight: 260, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all', background: '#f5f5f5', padding: 12, borderRadius: 4 }}>
                {formatJSON(currentLog.requestPayload)}
              </pre>
            </div>

            <div style={{ marginTop: 16 }}>
              <div style={{ fontWeight: 500, marginBottom: 8 }}>响应内容</div>
              <pre style={{ maxHeight: 260, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all', background: '#f5f5f5', padding: 12, borderRadius: 4 }}>
                {formatJSON(currentLog.responsePayload)}
              </pre>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default MeituanOrderConfirmLog;
